import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X as XIcon } from "@phosphor-icons/react";
import SantriSelect from "@/components/SantriSelect";
import RowActions from "@/components/RowActions";
import QuranPicker from "@/components/QuranPicker";
import { queueOp, flushQueue } from "@/lib/offlineSync";

// All setoran types
const TIPE = [
  ["ziyadah", "Ziyadah (Setoran Baru)"],
  ["manzil", "Manzil (Murajaah)"],
  ["setoran_murajaah", "Setoran Murajaah"],
  ["murajaah_fardiyah", "Murajaah Fardiyah"],
];
const NILAI_KEYS = ["nilai_kelancaran", "nilai_makhraj", "nilai_tajwid", "nilai_mad", "nilai_ketepatan", "nilai_kelengkapan", "nilai_adab"];

const EMPTY_FORM = {
  santri: null, tipe: "ziyadah", tanggal: new Date().toISOString().slice(0, 10),
  keterangan: "", halaman_awal: "", halaman_akhir: "",
  nilai_kelancaran: "", nilai_makhraj: "", nilai_tajwid: "", nilai_mad: "",
  nilai_ketepatan: "", nilai_kelengkapan: "", nilai_adab: "",
};

export default function Tahfiz() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [quran, setQuran] = useState({});
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [tipeFilter, setTipeFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("individu"); // individu | massal
  const [kelompokTipe, setKelompokTipe] = useState("halaqah"); // halaqah | kamar | kelas | all
  const [kelompokId, setKelompokId] = useState("");
  const [master, setMaster] = useState({ halaqah: [], kamar: [], kelas: [] });
  const [santriList, setSantriList] = useState([]);
  const [bulkRows, setBulkRows] = useState({}); // { nis: {surat, ayat_dari, ayat_sampai, halaman_awal, halaman_akhir, keterangan} }
  const [bulkTipe, setBulkTipe] = useState("ziyadah");
  const [bulkTanggal, setBulkTanggal] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const params = {};
    if (dateFilter.from) params.tanggal_from = dateFilter.from;
    if (dateFilter.to) params.tanggal_to = dateFilter.to;
    if (tipeFilter) params.tipe = tipeFilter;
    const { data } = await api.get("/tahfiz", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFilter, tipeFilter]);
  useEffect(() => {
    Promise.all([
      api.get("/master/halaqah"), api.get("/master/kamar"), api.get("/master/kelas"),
    ]).then(([h, ka, ke]) => setMaster({ halaqah: h.data, kamar: ka.data, kelas: ke.data }));
  }, []);

  // Load santri list when kelompok selected (for massal mode)
  useEffect(() => {
    if (mode !== "massal") return;
    (async () => {
      let params = {};
      if (kelompokTipe === "halaqah" && kelompokId) params.halaqah_id = kelompokId;
      else if (kelompokTipe === "kamar" && kelompokId) params.kamar_id = kelompokId;
      else if (kelompokTipe === "kelas" && kelompokId) params.kelas_id = kelompokId;
      else if (kelompokTipe !== "all") { setSantriList([]); return; }
      const { data } = await api.get("/santri", { params });
      setSantriList(data);
      // Initialize bulk rows
      const rows = {};
      data.forEach((s) => { rows[s.nis] = rows[s.nis] || {}; });
      setBulkRows(rows);
    })();
  }, [mode, kelompokTipe, kelompokId]);

  const buildPayload = (base, q) => {
    const p = { ...base };
    if (q?.surat) p.surat = q.surat;
    if (q?.juz) p.juz = q.juz;
    if (q?.ayat_dari) p.ayat_dari = Number(q.ayat_dari);
    if (q?.ayat_sampai) p.ayat_sampai = Number(q.ayat_sampai);
    if (q?.halaman) p.halaman = Number(q.halaman);
    NILAI_KEYS.forEach((k) => { if (p[k] === "" || p[k] === undefined) delete p[k]; else p[k] = Number(p[k]); });
    if (p.halaman_awal === "" || p.halaman_awal === undefined) delete p.halaman_awal; else p.halaman_awal = Number(p.halaman_awal);
    if (p.halaman_akhir === "" || p.halaman_akhir === undefined) delete p.halaman_akhir; else p.halaman_akhir = Number(p.halaman_akhir);
    return p;
  };

  const submitIndividu = async (e) => {
    e.preventDefault();
    const nis = form.santri?.nis;
    if (!nis) return toast.error("Pilih santri terlebih dahulu");
    payload = {
      ...form, nis,
      ...(quran?.mode === "range" ? {
        surah_dari: quran.surah_dari, surah_dari_nama: quran.surah_dari_nama,
        surah_sampai: quran.surah_sampai, surah_sampai_nama: quran.surah_sampai_nama,
        juz: quran.juz,
      } : {}),
    };
    if (quran?.mode !== "range") {
      if (quran?.surah_nama) payload.surat = quran.surah_nama;
      if (quran?.juz) payload.juz = quran.juz;
      if (quran?.ayat_dari) payload.ayat_dari = Number(quran.ayat_dari);
      if (quran?.ayat_sampai) payload.ayat_sampai = Number(quran.ayat_sampai);
    }
    NILAI_KEYS.forEach((k) => { if (payload[k] === "" || payload[k] === undefined) delete payload[k]; else payload[k] = Number(payload[k]); });
    if (payload.halaman_awal === "" || payload.halaman_awal === undefined) delete payload.halaman_awal; else payload.halaman_awal = Number(payload.halaman_awal);
    if (payload.halaman_akhir === "" || payload.halaman_akhir === undefined) delete payload.halaman_akhir; else payload.halaman_akhir = Number(payload.halaman_akhir);
    delete payload.santri;
    try {
      if (navigator.onLine) { await api.post("/tahfiz", payload); toast.success("Setoran tercatat"); }
      else { await queueOp({ op: "create", path: "/tahfiz", payload }); toast.success("Offline — antre sync"); }
      setForm(EMPTY_FORM); setQuran({});
      load(); flushQueue(api);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const submitBulk = async () => {
    const filled = Object.entries(bulkRows).filter(([_, v]) => v.surat || v.halaman_awal);
    if (filled.length === 0) return toast.error("Belum ada baris yang diisi");
    let ok = 0, fail = 0;
    for (const [nis, row] of filled) {
      try {
        const payload = {
          nis, tipe: bulkTipe, tanggal: bulkTanggal,
          surat: row.surat, juz: row.juz ? Number(row.juz) : undefined,
          ayat_dari: row.ayat_dari ? Number(row.ayat_dari) : undefined,
          ayat_sampai: row.ayat_sampai ? Number(row.ayat_sampai) : undefined,
          halaman_awal: row.halaman_awal ? Number(row.halaman_awal) : undefined,
          halaman_akhir: row.halaman_akhir ? Number(row.halaman_akhir) : undefined,
          keterangan: row.keterangan,
        };
        Object.keys(payload).forEach((k) => (payload[k] === undefined || payload[k] === "") && delete payload[k]);
        await api.post("/tahfiz", payload);
        ok++;
      } catch (_) { fail++; }
    }
    toast.success(`Berhasil: ${ok}${fail ? ` · Gagal: ${fail}` : ""}`);
    setBulkRows({}); load();
  };

  const doDelete = async (t) => { await api.delete(`/tahfiz/${t.id}`); toast.success("Dihapus"); load(); };
  const saveEdit = async () => {
    const payload = { tanggal: editing.tanggal, keterangan: editing.keterangan };
    ["surat", "juz", "ayat_dari", "ayat_sampai"].forEach((k) => { if (editing[k] !== undefined && editing[k] !== "") payload[k] = k === "surat" ? editing[k] : Number(editing[k]); });
    NILAI_KEYS.forEach((k) => { if (editing[k] !== undefined && editing[k] !== "" && editing[k] !== null) payload[k] = Number(editing[k]); });
    try {
      await api.put(`/tahfiz/${editing.id}`, payload);
      toast.success("Setoran diperbarui"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Divisi Tahfiz</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Setoran & Nilai Tahfiz</h1>
        <p className="text-sm text-slate-500 mt-1">Ziyadah, Manzil, Setoran Murajaah, Murajaah Fardiyah — Input <b>Massal</b> per Halaqah/Kamar/Kelas atau <b>Individu</b> per santri.</p>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        <button onClick={() => setMode("individu")} data-testid="tahfiz-mode-individu"
          className={`px-4 py-2 rounded-md text-sm font-medium ${mode === "individu" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Individu (Satu-per-satu)
        </button>
        <button onClick={() => setMode("massal")} data-testid="tahfiz-mode-massal"
          className={`px-4 py-2 rounded-md text-sm font-medium ${mode === "massal" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Massal (Per Halaqah/Kamar/Kelas)
        </button>
      </div>

      {mode === "individu" && (
        <form onSubmit={submitIndividu} className="imsyaf-card p-5 space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><label className="text-xs">Santri (nama/kamar) *</label>
              <SantriSelect value={form.santri} onChange={(s) => setForm({ ...form, santri: s })} testid="tahfiz-santri" /></div>
            <div><label className="text-xs">Jenis Tahfiz *</label>
              <select className="imsyaf-input" value={form.tipe} onChange={(e) => setForm({ ...form, tipe: e.target.value })} data-testid="tahfiz-tipe">
                {TIPE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#166534]">Pilih Bacaan (Al-Qur'an)</label>
            <QuranPicker value={quran} onChange={setQuran} />
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div><label className="text-xs">Halaman Awal (opsional)</label>
              <input type="number" step="0.5" className="imsyaf-input" value={form.halaman_awal} onChange={(e) => setForm({ ...form, halaman_awal: e.target.value })} /></div>
            <div><label className="text-xs">Halaman Akhir (opsional)</label>
              <input type="number" step="0.5" className="imsyaf-input" value={form.halaman_akhir} onChange={(e) => setForm({ ...form, halaman_akhir: e.target.value })} /></div>
            <div className="md:col-span-2"><label className="text-xs">Keterangan (opsional)</label>
              <input className="imsyaf-input" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></div>
          </div>
          {form.tipe === "ziyadah" && (
            <div>
              <div className="text-xs font-semibold text-[#166534] mb-2">Penilaian (7 komponen — bobot diatur di Pengaturan)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["nilai_kelancaran","Kelancaran"],["nilai_makhraj","Makhraj"],["nilai_tajwid","Tajwid"],["nilai_mad","Mad"],["nilai_ketepatan","Ketepatan"],["nilai_kelengkapan","Kelengkapan"],["nilai_adab","Adab"]].map(([k, l]) => (
                  <div key={k}><label className="text-xs">{l}</label>
                    <input type="number" min="0" max="100" className="imsyaf-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`tahfiz-${k}`} /></div>
                ))}
              </div>
            </div>
          )}
          <button className="imsyaf-btn-primary" data-testid="tahfiz-save-btn"><Plus size={16}/> Simpan Setoran</button>
        </form>
      )}

      {mode === "massal" && (
        <div className="imsyaf-card p-5 space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div><label className="text-xs">Pilih Kelompok</label>
              <select className="imsyaf-input" value={kelompokTipe} onChange={(e) => { setKelompokTipe(e.target.value); setKelompokId(""); }} data-testid="tahfiz-kelompok-tipe">
                <option value="halaqah">Per Halaqah</option><option value="kamar">Per Kamar</option>
                <option value="kelas">Per Kelas</option><option value="all">Semua Santri</option>
              </select></div>
            {kelompokTipe !== "all" && (
              <div><label className="text-xs">{kelompokTipe === "halaqah" ? "Halaqah" : kelompokTipe === "kamar" ? "Kamar" : "Kelas"}</label>
                <select className="imsyaf-input" value={kelompokId} onChange={(e) => setKelompokId(e.target.value)} data-testid="tahfiz-kelompok-id">
                  <option value="">— Pilih —</option>
                  {master[kelompokTipe].map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select></div>
            )}
            <div><label className="text-xs">Jenis Tahfiz</label>
              <select className="imsyaf-input" value={bulkTipe} onChange={(e) => setBulkTipe(e.target.value)} data-testid="tahfiz-bulk-tipe">
                {TIPE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={bulkTanggal} onChange={(e) => setBulkTanggal(e.target.value)} /></div>
          </div>

          {santriList.length === 0 ? (
            <div className="text-center py-8 text-slate-400">Pilih kelompok di atas untuk memuat daftar santri.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs zebra">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left px-2 py-2">Santri</th>
                    <th className="text-left px-2 py-2">Surat</th>
                    <th className="text-left px-2 py-2">Ayat</th>
                    <th className="text-left px-2 py-2">Juz</th>
                    <th className="text-left px-2 py-2">Halaman</th>
                    <th className="text-left px-2 py-2">Keterangan</th>
                  </tr></thead>
                  <tbody>
                    {santriList.map((s) => (
                      <tr key={s.nis} className="border-t border-slate-100" data-testid={`bulk-tahfiz-row-${s.nis}`}>
                        <td className="px-2 py-1 font-medium">{s.nama}<div className="text-[10px] font-mono text-slate-500">{s.kamar_nama || "-"}</div></td>
                        <td className="px-2 py-1"><input placeholder="Al-Baqarah" className="imsyaf-input text-xs" value={bulkRows[s.nis]?.surat || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], surat: e.target.value } })} /></td>
                        <td className="px-2 py-1"><div className="flex gap-1"><input type="number" placeholder="dari" className="imsyaf-input text-xs w-16" value={bulkRows[s.nis]?.ayat_dari || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], ayat_dari: e.target.value } })} /><input type="number" placeholder="sampai" className="imsyaf-input text-xs w-16" value={bulkRows[s.nis]?.ayat_sampai || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], ayat_sampai: e.target.value } })} /></div></td>
                        <td className="px-2 py-1"><input type="number" min="1" max="30" placeholder="Juz" className="imsyaf-input text-xs w-16" value={bulkRows[s.nis]?.juz || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], juz: e.target.value } })} /></td>
                        <td className="px-2 py-1"><div className="flex gap-1"><input type="number" placeholder="awal" className="imsyaf-input text-xs w-16" value={bulkRows[s.nis]?.halaman_awal || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], halaman_awal: e.target.value } })} /><input type="number" placeholder="akhir" className="imsyaf-input text-xs w-16" value={bulkRows[s.nis]?.halaman_akhir || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], halaman_akhir: e.target.value } })} /></div></td>
                        <td className="px-2 py-1"><input className="imsyaf-input text-xs" value={bulkRows[s.nis]?.keterangan || ""} onChange={(e) => setBulkRows({ ...bulkRows, [s.nis]: { ...bulkRows[s.nis], keterangan: e.target.value } })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-slate-500">
                Total santri: <b>{santriList.length}</b> — Isi hanya baris santri yang setor hari ini, sisanya biarkan kosong.
              </div>
              <button onClick={submitBulk} className="imsyaf-btn-primary" data-testid="tahfiz-bulk-save"><Plus size={16}/> Simpan Semua Setoran</button>
            </>
          )}
        </div>
      )}

      <div className="imsyaf-card p-4 flex flex-wrap items-end gap-3">
        <div><label className="text-xs">Dari</label><input type="date" className="imsyaf-input" value={dateFilter.from} onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })} /></div>
        <div><label className="text-xs">Sampai</label><input type="date" className="imsyaf-input" value={dateFilter.to} onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })} /></div>
        <div><label className="text-xs">Jenis</label>
          <select className="imsyaf-input" value={tipeFilter} onChange={(e) => setTipeFilter(e.target.value)}>
            <option value="">Semua</option>{TIPE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
        <div className="text-sm text-slate-500 pb-2 ml-auto">Total: <b>{items.length}</b></div>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Santri</th>
            <th className="text-left px-4 py-2">Jenis</th><th className="text-left px-4 py-2">Bacaan</th>
            <th className="text-left px-4 py-2">Nilai</th><th className="text-left px-4 py-2">Predikat</th>
            <th className="text-right px-4 py-2">Aksi</th>
          </tr></thead>
          <tbody>{items.map((t) => (
            <tr key={t.id} className="border-t border-slate-100" data-testid={`tahfiz-row-${t.id}`}>
              <td className="px-4 py-2">{t.tanggal}</td>
              <td className="px-4 py-2"><div className="font-medium">{t.santri_nama}</div><div className="text-xs font-mono text-slate-500">{t.santri_nis}</div></td>
              <td className="px-4 py-2"><span className="imsyaf-badge bg-green-50 text-green-800">{t.tipe}</span></td>
              <td className="px-4 py-2 text-xs">
                {t.surah_dari_nama && t.surah_sampai_nama ? (
                  <span className="font-medium text-[#166534]">{t.surah_dari_nama} → {t.surah_sampai_nama}</span>
                ) : (
                  <>
                    {t.surat && <>{t.surat} {t.ayat_dari ? `${t.ayat_dari}${t.ayat_sampai && t.ayat_sampai !== t.ayat_dari ? `-${t.ayat_sampai}` : ""}` : ""}</>}
                  </>
                )}
                {(t.halaman_awal || t.halaman_akhir) && <div className="text-slate-500">Hal {t.halaman_awal}{t.halaman_akhir && t.halaman_akhir !== t.halaman_awal ? `-${t.halaman_akhir}` : ""}</div>}
              </td>
              <td className="px-4 py-2 font-bold text-[#166534]">{t.nilai_total ?? "-"}</td>
              <td className="px-4 py-2">{t.predikat || "-"}</td>
              <td className="px-4 py-2 text-right">
                <RowActions testid={`tahfiz-${t.id}`}
                  onEdit={() => setEditing({ ...t })}
                  onDelete={() => doDelete(t)}
                  confirmText="Hapus setoran ini?" />
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Belum ada setoran.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="edit-tahfiz-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full space-y-3 relative">
            <button onClick={() => setEditing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><XIcon size={18}/></button>
            <h3 className="font-display text-lg font-bold text-[#064E3B]">Edit Setoran — {editing.santri_nama}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs">Tanggal</label>
                <input type="date" className="imsyaf-input" value={editing.tanggal} onChange={(e) => setEditing({ ...editing, tanggal: e.target.value })}/></div>
              <div><label className="text-xs">Surat</label>
                <input className="imsyaf-input" value={editing.surat || ""} onChange={(e) => setEditing({ ...editing, surat: e.target.value })}/></div>
              <div><label className="text-xs">Ayat Dari</label>
                <input type="number" className="imsyaf-input" value={editing.ayat_dari ?? ""} onChange={(e) => setEditing({ ...editing, ayat_dari: e.target.value })}/></div>
              <div><label className="text-xs">Ayat Sampai</label>
                <input type="number" className="imsyaf-input" value={editing.ayat_sampai ?? ""} onChange={(e) => setEditing({ ...editing, ayat_sampai: e.target.value })}/></div>
              <div><label className="text-xs">Juz</label>
                <input type="number" className="imsyaf-input" value={editing.juz ?? ""} onChange={(e) => setEditing({ ...editing, juz: e.target.value })}/></div>
              <div className="col-span-2"><label className="text-xs">Keterangan</label>
                <input className="imsyaf-input" value={editing.keterangan || ""} onChange={(e) => setEditing({ ...editing, keterangan: e.target.value })}/></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="imsyaf-btn-outline">Batal</button>
              <button onClick={saveEdit} className="imsyaf-btn-primary" data-testid="edit-tahfiz-save">Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
